// views/Dashboard/Profile/components/CasoCreateModal.js
import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  useToast,
  Text,
  Icon,
  Flex,
  HStack
} from '@chakra-ui/react';
import { FileText, User } from 'lucide-react';
import { casosService, casosUtils } from 'services/casosService';

const CasoCreateModal = ({ isOpen, onClose, customer, onCasoCreated }) => {
  // Estados del formulario
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    current_stage: 'recepcion',
    status: 'abierto'
  });
  const [loading, setLoading] = useState(false);

  const toast = useToast();

  // Manejar cambios en el formulario
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Validar formulario
  const validateForm = () => {
    if (!formData.title.trim()) {
      toast({
        title: 'Error de validación',
        description: 'El título del caso es requerido',
        status: 'error',
        duration: 4000,
        isClosable: true
      });
      return false;
    }

    if (!customer?.username) {
      toast({
        title: 'Error',
        description: 'No se puede crear caso: usuario no válido',
        status: 'error',
        duration: 4000,
        isClosable: true
      });
      return false;
    }

    return true;
  };

  // Manejar envío del formulario
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      console.log('🔄 CasoCreateModal - Creando caso:', formData);

      const casoData = {
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        respondent_username: customer.username,
        current_stage: formData.current_stage,
        status: formData.status,
        created_by: null // TODO: obtener del contexto de usuario
      };

      const response = await casosService.createCaso(casoData);

      if (response.success) {
        toast({
          title: 'Caso creado exitosamente',
          description: `El caso "${formData.title}" ha sido creado`,
          status: 'success',
          duration: 4000,
          isClosable: true
        });

        // Resetear formulario
        setFormData({
          title: '',
          description: '',
          current_stage: 'recepcion',
          status: 'abierto'
        });

        // Notificar al componente padre
        if (onCasoCreated) {
          onCasoCreated(response.data);
        }

        // Cerrar modal
        onClose();
      }
    } catch (error) {
      console.error('❌ CasoCreateModal - Error creando caso:', error);
      toast({
        title: 'Error al crear caso',
        description: error.message || 'Ha ocurrido un error inesperado',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setLoading(false);
    }
  };

  // Manejar cierre del modal
  const handleClose = () => {
    if (!loading) {
      // Resetear formulario solo si no está cargando
      setFormData({
        title: '',
        description: '',
        current_stage: 'recepcion',
        status: 'abierto'
      });
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      closeOnOverlayClick={!loading}
      isCentered
    >
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(6px)" />
      <ModalContent fontFamily="jakarta" borderRadius="18px" mx={3}>
        <ModalHeader>
          <HStack spacing={3}>
            <Flex w="38px" h="38px" borderRadius="11px" bg="coral.soft" align="center" justify="center" flexShrink={0}>
              <Icon as={FileText} color="coral.700" boxSize={5} />
            </Flex>
            <VStack align="start" spacing={0}>
              <Text fontSize="16px" fontWeight="800" color="ink.900">
                Crear nuevo caso
              </Text>
              <HStack spacing={1.5} fontSize="12px" color="ink.400">
                <Icon as={User} boxSize={3.5} />
                <Text>
                  Para {customer?.first_name} {customer?.last_name} ({customer?.username})
                </Text>
              </HStack>
            </VStack>
          </HStack>
        </ModalHeader>

        <ModalCloseButton isDisabled={loading} />

        <ModalBody pb={6}>
          <VStack spacing={4} align="stretch">
            {/* Título del caso */}
            <FormControl isRequired>
              <FormLabel color="ink.700" fontSize="12.5px" fontWeight="600">
                Título del caso
              </FormLabel>
              <Input
                placeholder="Ej: Solicitud de beca académica 2025"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                h="38px" borderRadius="10px" fontSize="13.5px"
                border="1.5px solid" borderColor="cream.line2"
                _focus={{ borderColor: 'coral.500', boxShadow: 'none' }}
                isDisabled={loading}
                maxLength={255}
              />
              <Text fontSize="11px" color="ink.400" mt={1}>
                {formData.title.length}/255 caracteres
              </Text>
            </FormControl>

            {/* Descripción */}
            <FormControl>
              <FormLabel color="ink.700" fontSize="12.5px" fontWeight="600">
                Descripción (opcional)
              </FormLabel>
              <Textarea
                placeholder="Describe brevemente el caso y los objetivos esperados..."
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                borderRadius="10px" fontSize="13.5px"
                border="1.5px solid" borderColor="cream.line2"
                _focus={{ borderColor: 'coral.500', boxShadow: 'none' }}
                isDisabled={loading}
                rows={3}
                resize="vertical"
                maxLength={500}
              />
              <Text fontSize="11px" color="ink.400" mt={1}>
                {formData.description.length}/500 caracteres
              </Text>
            </FormControl>

            {/* Stage inicial */}
            <FormControl>
              <FormLabel color="ink.700" fontSize="12.5px" fontWeight="600">
                Etapa inicial
              </FormLabel>
              <Select
                value={formData.current_stage}
                onChange={(e) => handleInputChange('current_stage', e.target.value)}
                h="38px" borderRadius="10px" fontSize="13.5px"
                border="1.5px solid" borderColor="cream.line2"
                _focus={{ borderColor: 'coral.500', boxShadow: 'none' }}
                isDisabled={loading}
              >
                {Object.entries(casosUtils.CASO_WORKFLOW_STAGES).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </Select>
              <Text fontSize="11px" color="ink.400" mt={1}>
                La mayoría de casos inician en "Recepción"
              </Text>
            </FormControl>

            {/* Status inicial */}
            <FormControl>
              <FormLabel color="ink.700" fontSize="12.5px" fontWeight="600">
                Estado inicial
              </FormLabel>
              <Select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                h="38px" borderRadius="10px" fontSize="13.5px"
                border="1.5px solid" borderColor="cream.line2"
                _focus={{ borderColor: 'coral.500', boxShadow: 'none' }}
                isDisabled={loading}
              >
                {Object.entries(casosUtils.CASO_STATUS_OPTIONS).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </Select>
              <Text fontSize="11px" color="ink.400" mt={1}>
                Los casos nuevos generalmente se crean como "Abierto"
              </Text>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter gap={2}>
          <Button variant="ghost" size="sm" onClick={handleClose} isDisabled={loading}>
            Cancelar
          </Button>
          <Button
            size="sm" borderRadius="9px" fontWeight="700"
            bg="coral.500" color="#fff" _hover={{ bg: 'coral.600' }}
            onClick={handleSubmit}
            isLoading={loading}
            loadingText="Creando caso..."
            leftIcon={<Icon as={FileText} boxSize="14px" />}
          >
            Crear caso
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CasoCreateModal;